import { Ayah, SoundFile } from '@app-types/KuranTypes'
import { FilterResult } from '@components/Home/HomePageSearchModal'
import { SurahType } from '@components/shared/List/List'
import { contentLibrary } from '@components/shared/consts'
import slugify from 'slugify'
import { getSurahPageHref } from './surahAnchor'

export const getAllAyahIds = (ayahs: Ayah[]): SoundFile[] => {
  const result: SoundFile[] = []

  ayahs?.forEach((ayah, i) => {
    // Insert Basmala before first ayah of every surah except Al-Fatiha
    if (ayah.verse_number === 1 && ayah.surah.id !== 1) {
      result.push({
        id: 1,
        surahName: 'Fatiha',
        surahIndex: 1,
        no: 1,
        highlightIndex: i,
      })
    }

    result.push({
      id: ayah.id,
      surahName: ayah.surah.name,
      surahIndex: ayah.surah.id,
      no: ayah.verse_number,
      highlightIndex: i,
    })
  })

  return result
}

export const getQuranSurahAyah = (
  word = '',
  surahs: SurahType[] = [],
): FilterResult[] => {
  if (!word) return []
  const [, surahName, ayahNumber] = word.match(/^(\w+)\s(\d+)/) || []

  if (surahName && ayahNumber) {
    const surah = surahs.find((surah) => checkIfWordExists(surahName, surah))
    if (surah) {
      const ayahToGo =
        surah.numberOfAyahs >= Number(ayahNumber) ? ayahNumber : surah.numberOfAyahs

      if (ayahToGo) {
        return [
          {
            name: `${surah.name} Suresi ${ayahToGo}. Ayet`,
            keywords: `${surahName} ${ayahToGo}`,
            url: `/ayet?sure=${surah.index}&ayet=${ayahToGo}`,
          },
        ]
      }
    }
  }

  // handles search like 3:32
  const [surahInputAsNumber, ayahInputAsNumber] = word.match(/\d+/g) || []

  if (surahInputAsNumber && ayahInputAsNumber) {
    const selectedSurah = surahs[Number(surahInputAsNumber) - 1]
    if (selectedSurah) {
      const ayahToShow =
        ayahInputAsNumber &&
        (selectedSurah.numberOfAyahs >= Number(ayahInputAsNumber)
          ? ayahInputAsNumber
          : selectedSurah.numberOfAyahs)

      const url = ayahToShow
        ? `/ayet?sure=${surahInputAsNumber}&ayet=${ayahToShow}`
        : getSurahPageHref(selectedSurah)
      return [
        {
          name: ayahToShow
            ? `${selectedSurah.name} Suresi,  ${ayahToShow}. Ayet`
            : `${selectedSurah.name} Suresi`,
          keywords: selectedSurah.name,
          url,
        },
      ]
    }
  }
  return []
}

export const checkIfWordExists = (
  word: string,
  data: { name: string; page?: number; index?: number; keywords?: string },
) => {
  if (!word) return false
  const partsToSearch =
    slugify(data.name, { lower: true }) +
    ' ' +
    slugify(data.keywords || '', { lower: true })
  return partsToSearch.includes(slugify(word.toLowerCase()))
}

const isWordsMatch = (word: string, data: { name: string; keywords?: string }) => {
  if (!word) return true
  const partsToSearch =
    slugify(data.name, { lower: true }) +
    ' ' +
    slugify(data.keywords || '', { lower: true })
  return partsToSearch.includes(slugify(word.toLowerCase()))
}

export const getDynamicSearchResults = (wordAsString: string): FilterResult[] => {
  const numberAsString = wordAsString.match(/\d+/g)
  const queriedText = wordAsString.replace(/\d+/g, '').trim()
  const results: FilterResult[] = []
  if (
    Number(numberAsString) > 0 &&
    Number(numberAsString) < 604 &&
    isWordsMatch(queriedText, { name: 'kuran kerim quran' })
  ) {
    results.push({
      name: `${numberAsString}. Sayfa - Kur'an`,
      url: `/kuran/${numberAsString}`,
      keywords: 'kuran kerim sayfa',
    })
  }

  if (
    Number(numberAsString) > 0 &&
    Number(numberAsString) < 31 &&
    isWordsMatch(queriedText, { name: 'kuran kerim cuz' })
  ) {
    const cuzPage = (Number(numberAsString) - 1) * 20 + 1
    results.push({
      name: `${numberAsString}. Cüz - Kur'an`,
      url: `/kuran/${cuzPage}`,
      keywords: 'kuran kerim cuz',
    })
  }

  if (
    Number(numberAsString) > 0 &&
    Number(numberAsString) < 101 &&
    isWordsMatch(queriedText, { name: 'cevsen serif' })
  ) {
    const cevsenPage = Math.ceil(Number(numberAsString) / 10)
    results.push({
      name: `${numberAsString}. Bâb - Cevşen`,
      url: `/cevsen/${cevsenPage}#${numberAsString}`,
      keywords: 'cevsen serif bab',
    })
  }

  contentLibrary.forEach((item) => {
    if (
      item.pageCount &&
      Number(numberAsString) > 0 &&
      Number(numberAsString) <= item.pageCount &&
      isWordsMatch(queriedText, item)
    ) {
      results.push({
        name: `${numberAsString}. ${item.pageName || 'Sayfa'} - ${item.name}`,
        url: `/${item.url}/${numberAsString}`,
        keywords: item.keywords || item.name,
      })
    }
  })

  return results
}
export const filterAndGet = (word: string, data: SurahType[]) => {
  if (word && data) {
    return data.filter((part) => {
      return checkIfWordExists(word, part)
    })
  } else {
    return data
  }
}

export const debounce = <T extends (...args: any[]) => ReturnType<T>>(
  callback: T,
  timeout: number,
): ((...args: Parameters<T>) => void) => {
  let timer: ReturnType<typeof setTimeout>

  return (...args: Parameters<T>) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      callback(...args)
    }, timeout)
  }
}
